/**
 * Hero — unified surface for State A (full marketing hero) and State B
 * (compact sticky header per Search Plus design).
 *
 * VOY-476: adds a `mode` prop. `full` keeps the v6 landing layout intact;
 * `compact` collapses to the Search Plus header block — small eyebrow,
 * trimmed H1 with "go," italic, two summary pill badges, then
 * SmartSearchCard mounted in `compact` mode. No globe, no marketing
 * CTAs, no stats meta in compact.
 *
 * TopNav (brand + nav links + theme toggle + avatar) renders in BOTH
 * modes — the Search Plus design HTML preserves the full top nav.
 * Don't remove TopNav from the page when toggling to compact; that's
 * LandingV6's responsibility in VOY-477.
 *
 * Mode switching is animated via a 250ms CSS transition declared on
 * `.hero` / `.hero.compact` in voy-v6.css. View-Transitions API is
 * intentionally not used (browser support is uneven).
 */
import { Link } from "react-router-dom";
import { Globe, GlobeOverlay } from "./Globe";
import { SmartSearchCard } from "@/components/search/SmartSearchCard";
import { useTripsSummary } from "@/hooks/useTripsSummary";
import type { SearchState } from "@/lib/search/types";
import type { useSearchState } from "@/lib/search/useSearchState";

import { ExampleLabel } from "./ExampleLabel";

export type HeroMode = "full" | "compact";

export interface HeroProps {
  /** Visual variant. `full` = State A, `compact` = State B. Defaults to full. */
  mode?: HeroMode;
  userFirstName?: string;
  onSubmit?: (state: SearchState) => void;
  /**
   * VOY-477: optional shared `useSearchState` instance. When LandingV6
   * owns the route state machine it threads its hook here so the inner
   * SmartSearchCard reads/writes the same state as the route.
   */
  search?: ReturnType<typeof useSearchState>;
}

export function Hero({ mode = "full", userFirstName, onSubmit, search }: HeroProps) {
  // Missing firstName → "you" keeps the H1 grammatical.
  const name = userFirstName?.trim() || "you";
  // Pills only render in compact mode — gate the query so full-mode
  // landings don't pay a Supabase round-trip for data they discard.
  const { savedCount, lastTrip } = useTripsSummary(mode === "compact");

  if (mode === "compact") {
    return (
      <section className="hero compact" id="hero" data-mode="compact">
        {/* Search Plus design's `.ph` header block: eyebrow + smaller H1
            + two pill badges. The H1 keeps the prompt copy but flips the
            italic accent from the name (full mode) to "go," to match the
            design exactly. */}
        <header className="hero-ph">
          <div className="hero-ph-row">
            <div>
              <div className="eyebrow">
                <span className="pulse" />
                SEARCH WITH <em className="serif-i">memory</em>
              </div>
              <h1>
                Where would you like to <span className="italic">go,</span> {name}?
              </h1>
            </div>
            <div className="hero-ph-pills">
              {savedCount > 0 && (
                <span className="hero-pill">· {savedCount} saved trip{savedCount === 1 ? '' : 's'}</span>
              )}
              {lastTrip && (
                <span className="hero-pill hero-pill-purple">· Last: {lastTrip.city}, {lastTrip.date}</span>
              )}
            </div>
          </div>
        </header>

        <div className="hero-compact-card">
          <SmartSearchCard mode="compact" onSubmit={onSubmit} search={search} />
        </div>
      </section>
    );
  }

  return (
    <section className="hero" id="hero" data-mode="full">
      <Globe />
      <GlobeOverlay />

      <div className="hero-grid">
        <div className="hero-copy">
          <div className="eyebrow">
            <span className="pulse" /> Live · Your Travel Second Brain
          </div>
          <h1>
            Where would you like to go, <span className="italic">{name}</span>?
          </h1>
          <p className="lede">
            The travel platform with memory. Tell us in plain language. Every search and booking becomes context,
            so the next trip starts already planned around you. Members pay zero markup on flights and hotels.
          </p>
        </div>

        <div className="hero-right">
          <SmartSearchCard mode="hero" onSubmit={onSubmit} search={search} />
        </div>

        <div className="hero-bottom">
          {/* PR #280 review note: the memory pills below are hardcoded
              placeholders. The compact-mode pills already have a
              TODO(VOY-476-followup) reference; same status applies here.
              "Last: Lisbon, May 27" will read as past after this week —
              follow-up ticket will wire all five (Home / Loyalty / Trips
              / Travels-with / Budget) to the user's saved profile data
              (probably `user_profiles` + a saved-search aggregate).
              TODO(VOY-477-followup): replace with live user data. */}
          <ExampleLabel />
          <div className="memory-pills">
            <span className="mpill">
              <span className="mp-label">Home</span>
              <span className="mp-val">SFO, BUE</span>
            </span>
            <span className="mp-dot">·</span>
            <span className="mpill">
              <span className="mp-label">Loyalty</span>
              <span className="mp-val">
                <em>United Premier 1K</em>
              </span>
            </span>
            <span className="mp-dot">·</span>
            <span className="mpill">
              <span className="mp-label">Trips</span>
              <span className="mp-val">14 logged</span>
            </span>
            <span className="mp-dot">·</span>
            <span className="mpill">
              <span className="mp-label">Travels with</span>
              <span className="mp-val">
                <em>Marisol</em>
              </span>
            </span>
            <span className="mp-dot mp-budget-dot">·</span>
            <span className="mpill mp-budget">
              <span className="mp-label">Budget</span>
              <span className="mp-val">$2,200 / trip</span>
            </span>
          </div>

          <div className="search-caption">
            Or tell us what you want next. <em>We'll plan it around who you are.</em>
          </div>

          <div className="hero-ctas">
            <Link to="/subscription" className="btn btn-primary">
              Start Saving with Voy
            </Link>
            <Link to="/you" className="btn btn-ghost">
              See your memory →
            </Link>
          </div>

          <div className="hero-meta">
            <div>
              <div className="m-k">0%</div>
              <div className="m-l">Member markup</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
